/**
 * Dwell observer — tracks how long elements matching a selector
 * remain visible in the viewport. Fires once per trigger ID.
 */

import { watchSelector } from "./mutation";
import type { EventMessage } from "../messages";

type OnEvent = (msg: EventMessage) => void;

interface DwellTrigger {
  id: string;
  selector: string;
  duration: number;
}

export function setupDwellObservers(
  triggers: DwellTrigger[],
  onEvent: OnEvent,
): () => void {
  const fired = new Set<string>();
  const timers = new Map<Element, ReturnType<typeof setTimeout>>();
  const observers: IntersectionObserver[] = [];

  for (const trigger of triggers) {
    watchSelector(trigger.selector, (elements) => {
      for (const el of elements) {
        const io = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (fired.has(trigger.id)) {
                io.unobserve(entry.target);
                return;
              }
              if (entry.isIntersecting) {
                const timer = setTimeout(() => {
                  if (fired.has(trigger.id)) return;
                  fired.add(trigger.id);
                  timers.delete(entry.target);
                  io.unobserve(entry.target);
                  onEvent({
                    type: "event",
                    event: "dwell",
                    triggerId: trigger.id,
                    selector: trigger.selector,
                    url: window.location.href,
                  });
                }, trigger.duration);
                timers.set(entry.target, timer);
              } else {
                const existing = timers.get(entry.target);
                if (existing != null) {
                  clearTimeout(existing);
                  timers.delete(entry.target);
                }
              }
            }
          },
          { threshold: 0 },
        );
        io.observe(el);
        observers.push(io);
      }
    });
  }

  return () => {
    for (const timer of timers.values()) clearTimeout(timer);
    timers.clear();
    for (const io of observers) io.disconnect();
  };
}
